// RunButton — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    position: relative;
    cursor: pointer;
    font-family: var(--run-font-semantic-family-body);
    font-weight: var(--run-font-semantic-weight-medium);
    line-height: var(--run-line-height-normal);
    text-decoration: none;
    border: none;
    outline: none;
    user-select: none;
    -webkit-user-select: none;
  }

  :host([size="sm"]) [part="base"] {
    block-size: var(--run-button-height-sm, 32px);
    padding-inline: var(--run-button-padding-x-sm, var(--run-space-semantic-sm));
    font-size: var(--run-button-font-size-sm, var(--run-font-size-sm));
    gap: var(--run-button-gap-sm, var(--run-space-semantic-2xs));
  }
  :host([size="md"]) [part="base"],
  :host(:not([size])) [part="base"] {
    block-size: var(--run-button-height-md, 40px);
    padding-inline: var(--run-button-padding-x-md, var(--run-space-semantic-md));
    font-size: var(--run-button-font-size-md, var(--run-font-size-base));
    gap: var(--run-button-gap-md, var(--run-space-semantic-xs));
  }
  :host([size="lg"]) [part="base"] {
    block-size: var(--run-button-height-lg, 48px);
    padding-inline: var(--run-button-padding-x-lg, var(--run-space-semantic-lg));
    font-size: var(--run-button-font-size-lg, var(--run-font-size-base));
    gap: var(--run-button-gap-lg, var(--run-space-semantic-xs));
  }

  :host([icon-only][size="sm"]) [part="base"] {
    inline-size: var(--run-button-height-sm, 32px);
    padding-inline: 0;
  }
  :host([icon-only][size="md"]) [part="base"],
  :host([icon-only]:not([size])) [part="base"] {
    inline-size: var(--run-button-height-md, 40px);
    padding-inline: 0;
  }
  :host([icon-only][size="lg"]) [part="base"] {
    inline-size: var(--run-button-height-lg, 48px);
    padding-inline: 0;
  }

  [part="base"] {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    inline-size: 100%;
    block-size: 100%;
    padding-block: 0;
    font: inherit;
    cursor: inherit;
    text-decoration: inherit;
    border-radius: var(--run-button-border-radius, var(--run-radius-semantic-control));
    border: 1px solid transparent;
    box-sizing: border-box;
    outline: none;
    background: transparent;
    color: inherit;
    transition:
      background-color var(--run-motion-semantic-micro-duration, 120ms) var(--run-motion-semantic-micro-easing, ease),
      color var(--run-motion-semantic-micro-duration, 120ms) var(--run-motion-semantic-micro-easing, ease),
      border-color var(--run-motion-semantic-micro-duration, 120ms) var(--run-motion-semantic-micro-easing, ease),
      opacity var(--run-motion-semantic-micro-duration, 120ms) var(--run-motion-semantic-micro-easing, ease);
  }

  :host([pill]) [part="base"] {
    border-radius: var(--run-button-border-radius-pill, var(--run-radius-semantic-pill));
  }

  :host([data-reduced-motion]) [part="base"] {
    transition: none;
  }

  /* ── Focus ring ── */
  [part="base"]:focus-visible {
    outline: 2px solid var(--run-color-focus-ring);
    outline-offset: 2px;
  }

  /* ── Icon sizing ── */
  [part="prefix"],
  [part="suffix"] {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }
  :host([size="sm"]) [part="prefix"],
  :host([size="sm"]) [part="suffix"] {
    inline-size: var(--run-button-icon-size-sm, var(--run-size-icon-sm));
    block-size: var(--run-button-icon-size-sm, var(--run-size-icon-sm));
  }
  :host([size="md"]) [part="prefix"],
  :host([size="md"]) [part="suffix"],
  :host(:not([size])) [part="prefix"],
  :host(:not([size])) [part="suffix"] {
    inline-size: var(--run-button-icon-size-md, var(--run-size-icon-md));
    block-size: var(--run-button-icon-size-md, var(--run-size-icon-md));
  }
  :host([size="lg"]) [part="prefix"],
  :host([size="lg"]) [part="suffix"] {
    inline-size: var(--run-button-icon-size-lg, var(--run-size-icon-lg));
    block-size: var(--run-button-icon-size-lg, var(--run-size-icon-lg));
  }

  /* ── Spinner ── */
  [part="spinner"] {
    display: none;
    inline-size: 1em;
    block-size: 1em;
    border: 2px solid currentColor;
    border-block-start-color: transparent;
    border-radius: 50%;
    animation: run-btn-spin 0.6s linear infinite;
    transition-delay: var(--run-button-loading-delay, 0ms);
    flex-shrink: 0;
  }
  :host([loading]) [part="spinner"] {
    display: inline-block;
  }
  :host([data-reduced-motion]) [part="spinner"] {
    animation: none;
    border: 2px solid currentColor;
  }
  @keyframes run-btn-spin {
    to { transform: rotate(360deg); }
  }

  /* ── Label ── */
  [part="label"] {
    display: inline-flex;
    align-items: center;
  }

  /* ── Live region ── */
  .live-region {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }

  /* ════════════════════════════════════════════
     VARIANT: primary
  ════════════════════════════════════════════ */
  :host([variant="primary"]) [part="base"],
  :host(:not([variant])) [part="base"] {
    background-color: var(--run-color-intent-primary-base);
    color: var(--run-color-intent-primary-on);
    border-color: var(--run-color-intent-primary-base);
  }
  :host([variant="primary"]) [part="base"]:hover,
  :host(:not([variant])) [part="base"]:hover {
    background-color: var(--run-color-intent-primary-hover);
    border-color: var(--run-color-intent-primary-hover);
  }
  :host([variant="primary"]) [part="base"]:active,
  :host(:not([variant])) [part="base"]:active {
    background-color: var(--run-color-intent-primary-active);
    border-color: var(--run-color-intent-primary-active);
  }

  /* ════════════════════════════════════════════
     VARIANT: secondary
  ════════════════════════════════════════════ */
  :host([variant="secondary"]) [part="base"] {
    background-color: var(--run-btn-secondary-bg, var(--run-color-intent-primary-subtle));
    color: var(--run-btn-secondary-fg, var(--run-color-text-primary));
    border-color: transparent;
  }
  :host([variant="secondary"]) [part="base"]:hover {
    background-color: var(--run-btn-secondary-bg-hover, var(--run-color-intent-primary-subtle));
    filter: brightness(0.95);
  }
  :host([variant="secondary"]) [part="base"]:active {
    background-color: var(--run-btn-secondary-bg-active, var(--run-color-intent-primary-subtle));
    filter: brightness(0.9);
  }

  /* ════════════════════════════════════════════
     VARIANT: outline
  ════════════════════════════════════════════ */
  :host([variant="outline"]) [part="base"] {
    background-color: transparent;
    color: var(--run-color-intent-primary-base);
    border-color: var(--run-color-intent-primary-base);
  }
  :host([variant="outline"]) [part="base"]:hover {
    background-color: var(--run-color-intent-primary-subtle);
  }
  :host([variant="outline"]) [part="base"]:active {
    background-color: var(--run-color-intent-primary-subtle);
    filter: brightness(0.95);
  }

  /* ════════════════════════════════════════════
     VARIANT: ghost
  ════════════════════════════════════════════ */
  :host([variant="ghost"]) [part="base"] {
    background-color: transparent;
    color: var(--run-color-text-primary);
    border-color: transparent;
  }
  :host([variant="ghost"]) [part="base"]:hover {
    background-color: var(--run-color-state-hover-overlay, rgba(0,0,0,0.06));
  }
  :host([variant="ghost"]) [part="base"]:active {
    background-color: var(--run-color-state-active-overlay, rgba(0,0,0,0.12));
  }

  /* ════════════════════════════════════════════
     VARIANT: danger
  ════════════════════════════════════════════ */
  :host([variant="danger"]) [part="base"] {
    background-color: var(--run-color-intent-danger-base);
    color: var(--run-color-intent-danger-on);
    border-color: var(--run-color-intent-danger-base);
  }
  :host([variant="danger"]) [part="base"]:hover {
    background-color: var(--run-color-intent-danger-hover);
    border-color: var(--run-color-intent-danger-hover);
  }
  :host([variant="danger"]) [part="base"]:active {
    background-color: var(--run-color-intent-danger-active);
    border-color: var(--run-color-intent-danger-active);
  }

  /* ════════════════════════════════════════════
     VARIANT: link
  ════════════════════════════════════════════ */
  :host([variant="link"]) [part="base"] {
    background-color: transparent;
    color: var(--run-btn-link-fg, var(--run-color-intent-primary-base));
    border-color: transparent;
    text-decoration: underline;
    block-size: auto;
    padding-block: 0;
    padding-inline: 0;
  }
  :host([variant="link"]) [part="base"]:hover {
    color: var(--run-btn-link-fg-hover, var(--run-color-intent-primary-hover));
  }
  :host([variant="link"]) [part="base"]:active {
    color: var(--run-btn-link-fg-active, var(--run-color-intent-primary-active));
  }

  /* ── Disabled ── */
  :host([disabled]) {
    cursor: not-allowed;
  }
  :host([disabled]) [part="base"] {
    opacity: 0.4;
    pointer-events: none;
  }

  /* ── Loading ── */
  :host([loading]) {
    cursor: wait;
  }
  :host([loading]) [part="base"] {
    pointer-events: none;
  }

  /* ── Stretch height ── */
  :host([stretch-height]) {
    block-size: auto;
    align-items: stretch;
  }
  :host([stretch-height]) [part="base"] {
    block-size: auto;
    align-items: stretch;
  }

  /* ── Position-aware seam (compound composition — Boundary Ownership Contract v1.0) ── */
  /* data-position set by run-input-group on children. Zero the joined-edge corners only. */
  :host([data-position="end"])   [part="base"] { border-start-start-radius: 0; border-end-start-radius: 0; }
  :host([data-position="start"]) [part="base"] { border-start-end-radius: 0;   border-end-end-radius: 0; }
`;

export function render(host) {
  const {
    variant,
    size,
    disabled,
    loading,
    loadingText,
    type,
    href,
    target,
    rel,
    download,
    pill,
    label,
    _iconOnly,
  } = host; // declared in MVA SkinModule.render contract

  const ariaDisabled = disabled ? 'aria-disabled="true"' : '';
  const ariaBusy = loading ? 'aria-busy="true"' : '';
  const ariaLabel = label ? `aria-label="${label}"` : '';
  const nativeDisabled = disabled ? 'disabled' : '';

  const labelSlotContent = loading && loadingText
    ? `<span part="label">${loadingText}</span>`
    : `<span part="label"><slot></slot></span>`;

  const innerContent = `
    <span part="prefix"><slot name="prefix"></slot></span>
    <span part="spinner" aria-hidden="true"></span>
    ${labelSlotContent}
    <span part="suffix"><slot name="suffix"></slot></span>
  `;

  if (href) {
    const targetAttr = target ? `target="${target}"` : '';
    const relAttr = rel ? `rel="${rel}"` : '';
    const downloadAttr = download ? `download="${download}"` : '';
    host.shadowRoot.innerHTML = `
      <a
        part="base"
        href="${href}"
        ${targetAttr}
        ${relAttr}
        ${downloadAttr}
        ${ariaDisabled}
        ${ariaBusy}
        ${ariaLabel}
      >${innerContent}</a>
      <div class="live-region" role="status" aria-live="polite" aria-atomic="true"></div>
    `;
  } else {
    host.shadowRoot.innerHTML = `
      <button
        part="base"
        type="${type}"
        ${nativeDisabled}
        ${ariaDisabled}
        ${ariaBusy}
        ${ariaLabel}
      >${innerContent}</button>
      <div class="live-region" role="status" aria-live="polite" aria-atomic="true"></div>
    `;
  }
}
